import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  getUserPermissions,
  isAuthInitialized,
  UserPermissions,
} from '../utils/permissionUtils';

// Re-export isAuthInitialized for use in other components
export { isAuthInitialized };

const emptyPermissions: UserPermissions = {
  role: '',
  permissionGroup: '',
  canEditDailyNotes: false,
  canEditOfficeAlerts: false,
  canEditInsurance: false,
  canEditPractices: false,
  canEditProviders: false,
  canEditAbsences: false,
  canEditClosures: false,
  canViewDisplayDate: false,
  canEditDisplayDate: false,
  isAdmin: false,
  canAccessFollowUpScheduling: false,
};

/**
 * User permissions after JWT + localStorage are aligned (same rules as
 * `getUserPermissions`, including JWT merge when claims are partial).
 */
export const usePermissions = () => {
  const { isAuthenticated, isReady: isAuthReady, accessToken, decoded } =
    useAuth();
  const [initializationTimedOut, setInitializationTimedOut] =
    useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setInitializationTimedOut(false);
      return;
    }

    if ((isAuthReady && Boolean(accessToken || decoded)) || isAuthInitialized()) {
      setInitializationTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitializationTimedOut(true);
      console.warn('[Auth] Permissions initialization timed out');
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, isAuthReady, accessToken, decoded]);

  const permissions = useMemo<UserPermissions>(() => {
    if (!isAuthenticated || initializationTimedOut) {
      return emptyPermissions;
    }

    if (!isAuthReady || (!accessToken && !decoded)) {
      return emptyPermissions;
    }

    return getUserPermissions();
  }, [isAuthenticated, initializationTimedOut, isAuthReady, accessToken, decoded]);

  const isReady = isAuthenticated
    ? ((isAuthReady && Boolean(accessToken || decoded)) || initializationTimedOut)
    : false;

  return {
    permissions,
    isReady,
    authError: initializationTimedOut
      ? 'Authentication initialization timed out. Please refresh.'
      : null,
    // Convenience methods
    isAdmin: () => permissions.isAdmin,
    canEditDailyNotes: () => permissions.canEditDailyNotes,
    canEditOfficeAlerts: () => permissions.canEditOfficeAlerts,
    canEditInsurance: () => permissions.canEditInsurance,
    canEditPractices: () => permissions.canEditPractices,
    canEditProviders: () => permissions.canEditProviders,
    canEditAbsences: () => permissions.canEditAbsences,
    canEditClosures: () => permissions.canEditClosures,
    canViewDisplayDate: () => permissions.canViewDisplayDate,
    canEditDisplayDate: () => permissions.canEditDisplayDate,
  };
};
